import { c as createFocusTrap } from './focus-trap.esm-CoH9VLSd.js';

class HeaderNavigation extends HTMLElement {
  /** @type {NodeListOf<HTMLElement>} */ #controllers = this.querySelectorAll(
    '[aria-controls][data-first-level-link]'
  );
  /** @type {HTMLElement|null} */ #activeItem;
  /** @type {AbortController|null} */ #scrollController = null;
  /** @type {import('focus-trap').FocusTrap} */ #focusTrap;

  connectedCallback() {
    this.#controllers.forEach((controller) => {
      controller.addEventListener('mouseenter', () => this.#handleMouseenter(controller));
      controller.addEventListener('mouseleave', () => this.#handleMouseleave(controller));
      controller.addEventListener('focus', () => this.#handleFocus(controller));
      controller.addEventListener('keydown', (event) => this.#handleKeydown(event, controller));
    });
    this.addEventListener('focusout', (e) => this.#handleFocusout(e));
  }

  /**
   * Handle controller mouseenter
   * @param {HTMLElement} controller
   */
  #handleMouseenter(controller) {
    this.#open(controller, controller.ariaControlsElements[0]);
  }

  /**
   * Handle controller mouseleave
   * @param {HTMLElement} controller
   */
  #handleMouseleave(controller) {
    const target = controller.ariaControlsElements[0];

    // If the target is not hovered, close the active item
    if (!target.matches(':hover')) return this.#close(controller, target);

    // If the target is hovered, add a mouseleave listener to close the active item once
    target.addEventListener('mouseleave', () => this.#close(controller, target), {
      once: true,
    });
  }

  /**
   * Handle controller keydown
   * @param {KeyboardEvent} event
   * @param {HTMLElement} controller
   */
  #handleKeydown(event, controller) {
    if (event.code !== 'Space') return;

    // Prevent default space key behavior
    event.preventDefault();

    const target = controller.ariaControlsElements[0];

    // If the active item is open, close it, otherwise open it
    this.#activeItem ? this.#close(controller, target) : this.#open(controller, target);

    // Create the focus trap
    this.#focusTrap = createFocusTrap(target, {
      onDeactivate: () => this.#close(controller, target),
    });

    // Activate the focus trap once the transition ends
    target.addEventListener('transitionend', () => this.#focusTrap.activate(), { once: true });
  }

  /**
   * Handle controller focus
   * @param {HTMLElement} controller
   */
  #handleFocus(controller) {
    const target = controller.ariaControlsElements[0];

    if (this.#activeItem) this.#close(this.#activeItem.controller, this.#activeItem.target);
    this.#open(controller, target);
  }

  /**
   * Handle menu focusout
   * @param {FocusEvent} e
   */
  #handleFocusout(e) {
    const { controller, target } = this.#activeItem;
    if (!controller || !target) return;
    if (controller === e.relatedTarget || target === e.relatedTarget) return;
    if (controller.contains(e.relatedTarget) || target.contains(e.relatedTarget)) return;
    this.#close(controller, target);
  }

  /**
   * Open the active item
   * @param {HTMLElement} controller
   * @param {HTMLElement} target
   */
  #open(controller, target) {
    this.#activeItem = { controller, target };

    controller.ariaExpanded = 'true';
    target.ariaHidden = 'false';

    // Add scroll listener
    this.#addScrollListener(controller, target);
  }

  /**
   * Close the active item
   * @param {HTMLElement} controller
   * @param {HTMLElement} target
   */
  #close(controller, target) {
    this.#activeItem = null;

    controller.ariaExpanded = 'false';
    target.ariaHidden = 'true';

    // Clean up scroll listener
    this.#scrollController?.abort();
    this.#scrollController = null;

    // Clean up focus trap
    if (this.#focusTrap) this.#focusTrap = null;
  }

  /**
   * Add scroll listener to the active item
   * @param {HTMLElement} controller
   * @param {HTMLElement} target
   */
  #addScrollListener(controller, target) {
    // Clean up any existing scroll listener
    this.#scrollController?.abort();

    this.#scrollController = new AbortController();
    window.addEventListener('scroll', () => this.#close(controller, target), {
      once: true,
      signal: this.#scrollController.signal,
    });
  }
}

customElements.define('header-navigation', HeaderNavigation);
